import type { Feature } from '@public-api-contracts/models/chat/ChatMessage';
import type { ProductMixAndMatchFeatureOptions } from '../../../api/models/FeatureOptions';
import type { ProductMixAndMatchFeatureItem } from '../../../models/features/main/ProductMixAndMatchFeatureItem';
import type { AppDispatch, RootState } from '../../../store/store';
import { apiClient } from '../../ApiClient';
import { getNewFeatureUuid } from '../../FeatureService';
import type { FeatureHandler } from '../FeatureHandler';
import type { Except } from 'type-fest';
import type { ChatMessage } from '../../../models/ChatMessage';

export const productMixAndMatchMainFeatureHandler = {
    async startFeatureItem(
        dispatch: AppDispatch,
        state: RootState,
        featureOptions: ProductMixAndMatchFeatureOptions
    ): Promise<void> {
        const { productId } = featureOptions;
        const productIds = [productId];
        const shouldOpenSidebar = !state.featureState.mainFeatureItem;
        const newMainFeatureItem: ProductMixAndMatchFeatureItem = {
            featureUuid: getNewFeatureUuid('product-mix-and-match'),
            featureType: 'product-mix-and-match',
            mixAndMatchWithProductIds: productIds,
            mixAndMatchWithProducts: [],
            loadingStatus: 'loading',
            groups: []
        };
        dispatch.featureState.setMenuFeatureItem(null);
        dispatch.featureState.setMainFeatureItem(newMainFeatureItem);
        dispatch.uiState.setMode('full');

        await dispatch.featureState.startFeature({
            featureType: 'chat',
            chatType: 'general'
        });
        const feature: Feature = {
            featureType: 'product-mix-and-match',
            request: {
                productIds
            }
        };
        const newPartialChatMessage = {
            role: 'assistant',
            content: {
                contentType: 'feature',
                startedBy: 'user-from-ui',
                status: 'loading',
                feature
            }
        } as const satisfies Except<ChatMessage, 'timestamp' | 'uuid'>;
        const newChatMessage = dispatch.featureState.appendNewChatMessage(
            newPartialChatMessage
        );
        try {
            const {
                jsonResult: { combinationWithProducts, combinationGroups }
            } = await apiClient.assistant.productMixAndMatch({
                body: { productIds }
            });
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                loadingStatus: 'success',
                mixAndMatchWithProducts: combinationWithProducts,
                groups: combinationGroups
            });
            if (newChatMessage) {
                dispatch.featureState.patchExistingChatMessage({
                    uuid: newChatMessage.uuid,
                    content: {
                        ...(newChatMessage.content as (typeof newPartialChatMessage)['content']),
                        status: 'success',
                        feature: {
                            ...feature,
                            result: {
                                combinationWithProducts,
                                combinationGroups
                            }
                        }
                    }
                });
            }

            if (shouldOpenSidebar) {
                dispatch.featureState.setSidebarExpanded(true);
            }
            void dispatch.featureState.processMessages({
                handleLoading: true
            });
        } catch {
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                loadingStatus: 'error'
            });
            if (newChatMessage) {
                dispatch.featureState.patchExistingChatMessage({
                    uuid: newChatMessage.uuid,
                    content: {
                        ...(newChatMessage.content as (typeof newPartialChatMessage)['content']),
                        status: 'error'
                    }
                });
            }
        }
    },
    restoreFeatureItem(
        dispatch: AppDispatch,
        state: RootState,
        feature: Extract<Feature, { featureType: 'product-mix-and-match' }>
    ): void {
        const restoredFeatureItem: ProductMixAndMatchFeatureItem = {
            featureType: 'product-mix-and-match',
            mixAndMatchWithProductIds: feature.request.productIds,
            mixAndMatchWithProducts:
                feature.result?.combinationWithProducts ?? [],
            groups: feature.result?.combinationGroups ?? [],
            featureUuid: getNewFeatureUuid('product-search-similar'),
            loadingStatus: 'success'
        };
        dispatch.featureState.setMainFeatureItem(restoredFeatureItem);
    }
} as const satisfies FeatureHandler<ProductMixAndMatchFeatureItem>;
